/**
 * Provider Status Helper
 * Utility functions for displaying provider status across the app
 */

import { ProviderStatus } from '../types/api.types';

export interface StatusInfo {
  label: string;
  color: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
  animate: boolean;
}

/**
 * Get display information for a provider status
 * @param status - The provider's current status
 * @returns StatusInfo object with display properties
 */
export const getProviderStatusInfo = (status?: ProviderStatus): StatusInfo => {
  switch (status) {
    case 'available':
      return {
        label: 'Available',
        color: 'green',
        bgColor: 'bg-green-700',
        textColor: 'text-white',
        dotColor: 'bg-white',
        animate: true,
      };
    case 'busy':
      return {
        label: 'Busy',
        color: 'red',
        bgColor: 'bg-red-700',
        textColor: 'text-white',
        dotColor: 'bg-white',
        animate: false,
      };
    case 'away':
      return {
        label: 'Away',
        color: 'yellow',
        bgColor: 'bg-yellow-500',
        textColor: 'text-yellow-950',
        dotColor: 'bg-yellow-900',
        animate: false,
      };
    case 'on_break':
      return {
        label: 'On Break',
        color: 'orange',
        bgColor: 'bg-orange-700',
        textColor: 'text-white',
        dotColor: 'bg-white',
        animate: false,
      };
    case 'offline':
      return {
        label: 'Offline',
        color: 'gray',
        bgColor: 'bg-gray-600',
        textColor: 'text-white',
        dotColor: 'bg-gray-300',
        animate: false,
      };
    default:
      // Default to available if no status set
      return {
        label: 'Available',
        color: 'green',
        bgColor: 'bg-green-700',
        textColor: 'text-white',
        dotColor: 'bg-white',
        animate: true,
      };
  }
};

/**
 * StatusBadge component classes for inline usage
 */
export const getStatusBadgeClasses = (status?: ProviderStatus): string => {
  const info = getProviderStatusInfo(status);
  return `${info.bgColor} ${info.textColor}`;
};

/**
 * Get status dot classes
 */
export const getStatusDotClasses = (status?: ProviderStatus): string => {
  const info = getProviderStatusInfo(status);
  return `${info.dotColor} ${info.animate ? 'animate-pulse' : ''}`;
};
